export enum QrSessionState {
  WAITING_NOTIFY = 0,
  WAITING_PAY = 1,
  /** 電子支付 mpay 之後、confirm 回跳之前的中繼態,「正在付款」 */
  PAYING = 2,
  PAID = 3,
  DONE = 4,
  /** machine-ack 後、建單前:販賣機已就緒,依 flow 顯示選付款方式 / 等投幣 / 等機台建單 */
  MACHINE_READY = 5,
  CANCELED = 11,
  EXPIRED = 12,
  KICKED = 13,
}

export const TERMINAL_STATES: ReadonlySet<QrSessionState> = new Set([
  QrSessionState.DONE,
  QrSessionState.CANCELED,
  QrSessionState.EXPIRED,
  QrSessionState.KICKED,
])

export interface QrSessionVo {
  sessionId: string
  posMachineCode: string
  state: QrSessionState
  stateLabel: string
  orderId?: string
  warehouseNumber?: string | number
  productName?: string
  amount?: number
  /** PAYING 狀態時,server 會帶回使用者選的支付通路(如 'line-pay') */
  payChannel?: PayChannel
  /** 發票開立後 server 會帶回;'3J0002' = 手機載具、'-' = 捐贈 */
  carrierType?: CarrierType
  /** 是否支援開立發票;false → 手機端跳過發票流程,直接顯示「交易已結束」 */
  supportInvoice?: boolean
  /**
   * 儲值選物(-3)輸入金額畫面的常用金額快選。
   * ⚠️ **只有 createSession 回傳**,poll(getSession)不帶 —— 需在建立 session 當下取出並持久化。
   * 後端 machine 未設定時為預設 [20,25,30,35](= DEFAULT_COMMON_AMOUNTS)。
   */
  commonAmounts?: number[]
  /**
   * 該機台可用的支付通路(PayChannel value,如 'line-pay' / 'jko-pay' / 'ecoin-pay' / 'cash')。
   * 選電子支付後據此動態顯示通路選單。
   * ⚠️ 與 commonAmounts 同款:**只有 createSession 回傳**,poll 不帶 —— 需取出並持久化。
   * 空 / 未帶 → 前端 fallback 顯示所有已支援通路。
   */
  mpayList?: string[]
  /**
   * 販賣機回報「可交易」的時間(ISO datetime)。
   * WAITING_NOTIFY 時:null → 顯示「連線販賣機中」;有值 → 顯示「電子支付 / 現金」選項。
   * poll 回傳,可據此還原畫面(refresh 後也能繼續)。
   */
  machineAckedAt?: string | null
  /**
   * 手機端已選的付款方式。null → 尚未選;'mpay' → 電子支付(輸入金額建單);'cash' → 現金(等販賣機上報)。
   * poll 回傳。
   */
  payMethod?: QrPayMethod | null
  /**
   * 無螢幕機交易流程(依機型 snapshot,createSession + poll 都回傳)。決定 machine-ack 後手機顯示哪種畫面:
   * - 1 = PHONE_DECIDE(模擬投幣,主流程):跳「電子/現金」選項,電子由手機輸入金額建單
   * - 0 = CASH_ONLY(只收現金):直接「請投幣」,不呼 /pay-method
   * - 2 = MACHINE_DECIDE(富士/三電):顯示「請於販賣機操作」等待,金額由機台決定/建單,手機只跑後續付款
   * 未帶 → 視為 phone-decide(1)。
   */
  flow?: NoScreenFlow
}

/** 手機端付款方式(machine-ack 後選):對應後端 QrPayMethod enum,request/response 皆小寫字串 */
export type QrPayMethod = 'mpay' | 'cash'

/** 無螢幕機交易流程,對應後端 NoScreenFlow enum(整數序列化) */
export enum NoScreenFlow {
  CASH_ONLY = 0, // 只收現金
  PHONE_DECIDE = 1, // 手機決定金額(主流程)
  MACHINE_DECIDE = 2, // 機台決定金額
}

/** 後端 machine 未設定常用金額時的前端 fallback,與 server DEFAULT_COMMON_AMOUNTS 一致 */
export const DEFAULT_COMMON_AMOUNTS: readonly number[] = [20, 25, 30, 35]

export type PayChannel = 'line-pay' | 'jko-pay' | string

export interface QrMpayBo {
  /** 對應後端 PaymentBo:payTime、payChannel;PaymentOnlineBo:confirmUrl / cancelUrl / remark */
  payChannel: PayChannel
  payTime: string
  confirmUrl?: string
  cancelUrl?: string
  remark?: string
  /**
   * 用戶輸入的付款金額(正整數)。
   * - 新流程:選電子支付後手機端帶 amount → server 據此建 `-3` 訂單再下單(必帶)。
   * - 舊流程相容:session 已由機台建單(WAITING_PAY)時不帶,直接付款;一般商品訂單帶了會被拒。
   */
  amount?: number
}

/** 儲值選物 sentinel:WAITING_PAY 時 warehouseNumber === WAREHOUSE_STORED_VALUE 代表要手機輸入金額 */
export const WAREHOUSE_STORED_VALUE = '-3'

/**
 * `POST /session/{sessionId}/mpay` 的回應。
 * 對應後端 `OnlinePayCreateVo`。
 *   - paymentUrlWeb:一般瀏覽器用的付款頁 URL(https://...)
 *   - paymentUrlApp:手機 App 深連結(如 `line://pay/...`),若使用者有裝對應 App 可直接開啟
 */
export interface OnlinePayCreateVo {
  orderId?: string
  channelOrderId?: string
  paymentUrlWeb?: string
  paymentUrlApp?: string
  [key: string]: unknown
}

export function pickRedirectUrl(vo: OnlinePayCreateVo | null | undefined): string | null {
  if (!vo) return null
  return vo.paymentUrlWeb || vo.paymentUrlApp || null
}

/** 後端收的載具碼:'3J0002' = 手機條碼載具、'-' = 捐贈 */
export type CarrierType = '3J0002' | '-' | string

export const CARRIER_MOBILE = '3J0002'
export const CARRIER_DONATE = '-'

export interface QrInvoiceBo {
  carrierType?: CarrierType
  carrierId?: string
  businessId?: string
  email?: string
}

export interface ApiResp<T> {
  code: string
  message?: string
  data: T
}

export const RESP_OK = '000000'
